import './chartSettings.less';
import {observer} from 'mobx-react';
import React, {Component, useMemo} from 'react';
import {makeAutoObservable} from "mobx";
import {Section} from 'controls/react/layout/section'
import {Toolbar, ToolbarItemPosition} from 'controls/react/layout/toolbar'
import {AntTabs} from "controls/react/ant/antTabs";
import {AntButton} from "controls/react/ant/antButton";
import {FormEntryNew, HelpIcon} from "controls/react/form/formEntryNew";
import {AntCheckbox} from "controls/react/ant/antCheckbox";
import {AntSelect} from "controls/react/ant/antSelect";
import {AntInputNumber} from "controls/react/ant/antInputNumber";
import {MobxManager} from 'framework/mobx-integration';
import {BoxView} from 'controls/react/layout/boxView';
import {MetricUnitDropDown} from "areas/metrics/components/MetricSelector/components/MetricUnitDropDown";
import {legendDataSource} from "areas/metrics/components/MetricLegendSelect/constants";
import {tooltipDataSource} from "areas/metrics/components/MetricTooltipSelect/constants";
import {MetricLegendSelect} from "areas/metrics/components/MetricLegendSelect";
import {MetricTooltipSelect} from "areas/metrics/components/MetricTooltipSelect";


let b = require('b_').with('chart-settings');

const i18n = require('core/localization/localization').translator();

export type ChartSettings = {
	ignoreMissingData: boolean,
	showRegression: boolean,
	showThreshold: boolean,
	hideErrors: boolean,
	chartType: string,
	aggregationType: string,
	maxType: string,
	minType: string,
	maxValue: number | undefined,
	minValue: number | undefined,
	unitType: string,
	defaultQualifierUnitType: string,
	tooltipTemplate: string[],
	labelTemplate: string[]
}

export type CartSettingsProps = {
	value?: ChartSettings;
	flags: ChartSettingsFlags;
	onChange?: (value: ChartSettings) => void;
}

export type ChartSettingsFlags = {
	hasAggregateSelector: boolean,
	hasChartTypeSelector: boolean,
	hasMissingData: boolean,
	hasRegression: boolean,
	hasThreshold: boolean,
	hasMetricUnit: boolean
}

export const ChartSettings = observer(class CartSettings extends Component<CartSettingsProps> {
	store: ChartSettingsStore;
	tabs;

	constructor(props: CartSettingsProps) {
		super(props);
		this.store = new ChartSettingsStore(props);
		this.tabs = [{
			key: 'chart',
			label: i18n('Chart'),
			closable: false,
			children: <ChartTab store={this.store} flags={this.props.flags}/>
		}, {
			key: 'information',
			label: i18n('Information'),
			closable: false,
			children: <InformationTab store={this.store}/>
		}]
	}

	render() {
		return <AntTabs type={'card'} hideAdd={true} className={b()} items={this.tabs}/>
	}
});

const InformationTab = observer(({store}: { store: ChartSettingsStore }) => {
	return <Section appearance={'none'}>
		<Section appearance={'none'} contentPadding={true} childrenPadding={true}>
			<FormEntryNew model={store.value} modelField={'labelTemplate'} label={i18n('Legend')}>
				<MetricLegendSelect />
			</FormEntryNew>
			<FormEntryNew model={store.value} modelField={'tooltipTemplate'} label={i18n('Tooltip')}>
				<MetricTooltipSelect />
			</FormEntryNew>
		</Section>
		<Toolbar>
			<BoxView type={'info-2'} border={true}
			         position={ToolbarItemPosition.BEFORE_TITLE}>{i18n('Changes are persisted')}</BoxView>
			<AntButton onClick={store.onUpdate} type={"primary"}
			           position={ToolbarItemPosition.AT_THE_END}>{i18n('Update')}</AntButton>
			<AntButton onClick={store.onCancel} position={ToolbarItemPosition.AT_THE_END}>{i18n('Cancel')}</AntButton>
		</Toolbar>
	</Section>
});

const ChartTab = observer(({store, flags}: { store: ChartSettingsStore, flags: ChartSettingsFlags }) => {
	return <Section appearance={'none'}>
		<Section appearance={'none'} direction={'row'} contentPadding={true}>
			<Section appearance={'none'} direction={'column'} width={'oneThird'}>
				{flags.hasMissingData && <FormEntryNew model={store.value} modelField={'ignoreMissingData'}>
					<AntCheckbox>{i18n('Ignore missing data')}</AntCheckbox>
				</FormEntryNew>}
				<FormEntryNew model={store.value} modelField={'hideErrors'}>
					<AntCheckbox>{i18n('Hide errors')}</AntCheckbox>
				</FormEntryNew>
				{flags.hasRegression && <FormEntryNew model={store.value} modelField={'showRegression'}>
					<AntCheckbox>{i18n('Show trend')}</AntCheckbox>
				</FormEntryNew>}
				{flags.hasThreshold && <FormEntryNew model={store.value} modelField={'showThreshold'}>
					<AntCheckbox>{i18n('Show threshold')}</AntCheckbox>
				</FormEntryNew>}
			</Section>
			<Section appearance={'none'} contentClass={b('selectors')}>
				{flags.hasMetricUnit && <><FormEntryNew model={store.value} modelField={'unitType'}
				                                        label={i18n('Unit')}>
					<MetricUnitDropDown
						unitType={store.value.defaultQualifierUnitType}
						withCustom={false}
					/>
				</FormEntryNew>
					{/*This is a hack to keep alignment*/}
					<div></div>
				</>}
				{flags.hasAggregateSelector &&
					<FormEntryNew model={store.value} modelField={'aggregationType'} label={i18n('Aggregation')}
					              headerAdditionalContent={<HelpIcon tooltip={i18n('AGGREGATION_LABEL')}/>}>
						<AntSelect options={store.aggregationOptions}/>
					</FormEntryNew>}
				{!flags.hasAggregateSelector && <div></div>}
				{flags.hasChartTypeSelector &&
					<FormEntryNew model={store.value} modelField={'chartType'} label={i18n('Chart type')}>
						<AntSelect options={store.chartTypeOptions}/>
					</FormEntryNew>}
				{!flags.hasChartTypeSelector && <div></div>}
				<FormEntryNew model={store.value} modelField={'maxType'} label={i18n('Y-Axis Max')}
				              headerAdditionalContent={<HelpIcon tooltip={i18n('MAX_LABEL')}/>}>
					<AntSelect options={store.restrictionTypeOptions}/>
				</FormEntryNew>
				<FormEntryNew model={store.value} modelField={'maxValue'} label={i18n('Y-Axis Max')}
				              disabled={store.value.maxType == 'dynamic'}>
					<AntInputNumber/>
				</FormEntryNew>
				<FormEntryNew model={store.value} modelField={'minType'} label={i18n('Y-Axis Min')}
				              headerAdditionalContent={<HelpIcon tooltip={i18n('MIN_LABEL')}/>}>
					<AntSelect options={store.restrictionTypeOptions}/>
				</FormEntryNew>
				<FormEntryNew model={store.value} modelField={'minValue'} label={i18n('Y-Axis Min')}
				              disabled={store.value.minType == 'dynamic'}>
					<AntInputNumber/>
				</FormEntryNew>
			</Section>
		</Section>
		<Toolbar>
			<BoxView type={'info-2'} border={true}
			         position={ToolbarItemPosition.BEFORE_TITLE}>{i18n('Settings are temporary and apply only in this session.')}</BoxView>
			<AntButton onClick={store.onClose} type={"primary"}
			           position={ToolbarItemPosition.AT_THE_END}>{i18n('Close')}</AntButton>
			<AntButton onClick={store.onCancel} position={ToolbarItemPosition.AT_THE_END}>{i18n('Cancel')}</AntButton>
		</Toolbar>
	</Section>
})

export const getRestrictionTypeOptions = () => {
	return  [{
		label: i18n('Static'),
		value: 'static'
	}, {
		label: i18n('Dynamic'),
		value: 'dynamic'
	}]
}

class ChartSettingsStore {
	value: ChartSettings;

	aggregationOptions = [{
		label: i18n('High'),
		value: 'high'
	}, {
		label: i18n('Low'),
		value: 'low'
	}, {
		label: i18n('Average'),
		value: 'average'
	}];
	chartTypeOptions = [{
		label: i18n('Line'),
		value: 'line'
	}, {
		label: i18n('Area'),
		value: 'area'
	}, {
		label: i18n('Range'),
		value: 'area'
	}];
	restrictionTypeOptions = getRestrictionTypeOptions();

	mobx = new MobxManager();

	constructor(private props: CartSettingsProps) {
		makeAutoObservable(this);
		this.initEmptyValues();
		this.value = {...this.props.value};
		this.mobx.reaction(() => this.value.maxType, () => {
			if (this.value.maxType == 'dynamic') {
				this.value.maxValue = undefined;
			}
			if (this.value.maxType == 'static') {
				this.value.maxValue = 1000;
			}
		});
		this.mobx.reaction(() => this.value.minType, () => {
			if (this.value.minType == 'dynamic') {
				this.value.minValue = undefined;
			}
			if (this.value.minType == 'static') {
				this.value.minValue = 0;
			}
		})
	}

	initEmptyValues() {
		this.props.flags.hasAggregateSelector = this.props.flags.hasAggregateSelector ?? true;
		this.props.flags.hasChartTypeSelector = this.props.flags.hasChartTypeSelector ?? true;
		this.props.flags.hasMissingData = this.props.flags.hasMissingData ?? true;
		this.props.flags.hasThreshold = this.props.flags.hasThreshold ?? true;
		this.props.flags.hasRegression = this.props.flags.hasRegression ?? true;

		this.props.value.chartType = this.props.value.chartType ?? 'line';
		this.props.value.maxType = this.props.value.maxType ?? 'dynamic';
		this.props.value.minType = this.props.value.minType ?? 'static';
		this.props.value.minValue = this.props.value.minValue ?? 0;
	}

	onClose = () => {
		this.props.onChange?.(this.value);
	}
	onCancel = () => {
		this.props.onChange?.(this.props.value);
	}
	onUpdate = () => {
		this.onClose();
	}

	destroy() {
		this.mobx.destroy();
	}
}
